import './SearchResultList.scss'

import List, { DividerProps } from 'components/App/components/List'
import { ScrollableContainer } from 'components/App/components/ScrollableContainer'
import { EmptyResults } from 'components/EmptyResults'
import { ResultKind, SearchResultItem } from 'components/SearchResultItem'
import { useAppSelector } from 'data/store/hooks'
import { i18n } from 'lib/i18n'
import { isAddressItem, isQueryItem, ResultItem } from 'map/services'
import React, { useCallback, useEffect, useState } from 'react'
import { formatDistance } from 'utils/formatters'

export type Item = ResultItem & {
  link: string
  distanceLabel?: string
  categoryId?: string
  iconName?: string
  thumbnail?: string
  averageRating?: number
  reviewCount?: number
  isHighlighted: boolean
  distance?: number
}

interface SearchResultListProps {
  isMyLocationShared: boolean
  mapSearchParamStr: string | null
  resultItemList: ResultItem[]
  resultsKind: ResultKind
  onItemClick: (item: Item, event: React.MouseEvent<HTMLElement, MouseEvent>) => void
  onItemMouseDown?: (item: Item) => void
}

function getResultItemLink(resultItem: ResultItem, mapSearchParamStr: string | null): string {
  const link = isQueryItem(resultItem) ? `/discover/${resultItem.title}` : `/p/${resultItem.id}`
  return mapSearchParamStr === null ? link : `${link}?map=${mapSearchParamStr}`
}

export const SearchResultList = ({
  isMyLocationShared,
  mapSearchParamStr,
  resultItemList,
  resultsKind,
  onItemClick,
  onItemMouseDown,
}: SearchResultListProps): JSX.Element | null => {
  const [highlightedResultItemId, setHighlightedResultItemId] = useState<string>('')
  const countryCode = useAppSelector((state) => state.user.countryCode)
  const unit = useAppSelector((state) => state.app.unit)
  const [itemList, setItemList] = useState<Item[]>([])

  const handleItemChange = useCallback((): void => {
    setItemList(
      resultItemList.map((resultItem) => ({
        ...resultItem,
        isHighlighted: resultItem.id === highlightedResultItemId,
        link: getResultItemLink(resultItem, mapSearchParamStr),
        ...(isQueryItem(resultItem) && {
          iconName: 'search',
        }),
        ...(isAddressItem(resultItem) && {
          iconName: 'point',
        }),
        ...('categories' in resultItem && {
          categoryId: resultItem.categories.filter(({ primary }) => primary)[0]?.id,
        }),
        ...('distance' in resultItem &&
          isMyLocationShared && {
            distanceLabel: formatDistance(resultItem.distance, { countryCode, unit }),
          }),
        ...('media' in resultItem &&
          resultItem.media?.images?.items &&
          resultItem.media?.images?.items?.length > 0 && {
            thumbnail: resultItem.media.images.items[0].href,
          }),
        ...('media' in resultItem &&
          resultItem.media?.ratings?.items &&
          resultItem.media?.ratings?.items?.length && {
            averageRating: resultItem.media.ratings.items[0].average,
            reviewCount: resultItem.media.ratings.items[0].count,
          }),
      }))
    )
  }, [
    countryCode,
    highlightedResultItemId,
    isMyLocationShared,
    mapSearchParamStr,
    resultItemList,
    unit,
  ])

  useEffect(handleItemChange, [unit, countryCode, handleItemChange])

  const dividerOptions: DividerProps = { position: { left: '3rem' } }

  if (!itemList.length) {
    return resultsKind === 'search' ? (
      <EmptyResults
        title={i18n(`no-results-card.have-another-go`)}
        message={i18n(`no-results-card.message-label-search`)}
      />
    ) : null
  }

  return (
    <div className="SearchResultList-container">
      <ScrollableContainer className="SearchResultList-scrollable-container">
        <List className="SearchResultList" divider={dividerOptions}>
          {itemList.map((item, index) => (
            <SearchResultItem
              key={index}
              iconName={item.iconName}
              categoryId={item.categoryId}
              kind={resultsKind}
              title={item.title}
              link={mapSearchParamStr !== null ? item.link : ''}
              thumbnail={item.thumbnail}
              averageRating={item.averageRating}
              reviewCount={item.reviewCount}
              highlightedSubtitle={item.distanceLabel}
              subtitle={'address' in item ? item.address.label : undefined}
              isHighlighted={item.isHighlighted}
              onClick={(event) => onItemClick(item, event)}
              onMouseEnter={() => setHighlightedResultItemId(item.id)}
              onMouseLeave={() => setHighlightedResultItemId('')}
              onMouseDown={() => onItemMouseDown && onItemMouseDown(item)}
            />
          ))}
        </List>
      </ScrollableContainer>
    </div>
  )
}
